/**
 * The licence field's "i" (client, 2026-09-19: *"let's have an 'i' there so we explain the
 * various licence types so users are informed as to which one to use"*).
 *
 * A small round button beside the label toggles a panel that names each licence the select
 * offers and says, in one or two lines, what a reader of the data may then do with it. It
 * is a disclosure, not a tooltip: a hover-only note cannot be read on a phone, and the
 * choice it informs is one the submitter makes once and the catalogue shows forever.
 *
 * The same component sits in the wizard's metadata step and in the Update Metadata form,
 * because the same select is in both and a licence means the same thing in either place.
 */
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '../../lib/icons';
import { LICENCES } from './manager';

/** Translation key suffix per licence — the names are proper names, the keys are not. */
const LICENCE_KEY: Record<(typeof LICENCES)[number], string> = {
  'CC-BY-4.0': 'ccBy',
  'CC-BY-SA-4.0': 'ccBySa',
  ODbL: 'odbl',
  'Programme data': 'programme',
  Other: 'other',
};

export function LicenceInfo() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const panelId = useId();

  return (
    <span className="mgr-licinfo">
      <button
        type="button"
        className={open ? 'mgr-licinfo-btn on' : 'mgr-licinfo-btn'}
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={t('manager.licence.about')}
        title={t('manager.licence.about')}
        data-testid="mgr-licence-info"
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name="info" size={13} />
      </button>
      {open ? (
        <div className="mgr-licinfo-panel" id={panelId} role="region" data-testid="mgr-licence-help">
          <p className="mgr-licinfo-lead">{t('manager.licence.lead')}</p>
          <dl>
            {LICENCES.map((name) => (
              <div key={name} className="mgr-licinfo-row">
                <dt>{name}</dt>
                <dd>{t(`manager.licence.${LICENCE_KEY[name]}`)}</dd>
              </div>
            ))}
          </dl>
        </div>
      ) : null}
    </span>
  );
}

export default LicenceInfo;
